import { FC, useCallback, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography } from '@altermaind/web/ui';
import { Slider } from '@/_shared/components/Slider';
import { Tooltip } from '@/_shared/components/Tooltip';
import { useDialog } from '@/_shared/hooks/useDialog';
import { Content, getAdminContentsLikesByContentTypeCall } from '../../../../contentsLikes';
import { CardItem } from '../../../state';
import { CourseDetailsDialog } from './CourseDetailsDialog';
import { UsersContentsLikesDialog } from './UsersContentsLikesDialog';

type Props = {
  metricsMap: CardItem[];
  elementToView?: {
    desktopLarge?: number;
    desktop?: number;
    tablet?: number;
    mobile?: number;
  };
};

export const CardsPlayground: FC<Props> = ({ metricsMap, elementToView }) => {
  const t = useTranslations('aiPlayground.admin.landingPlayground.courses');

  const [parameters, setParameters] = useState<CardItem>();

  const { openDialog: openCourseDetailsDialog } = useDialog('courseDetails');
  const { openDialog: openUsersDetailsDialog } = useDialog('usersContentsLikesDialog');

  const { response: responseGetAdminContentsLikesByContentType = [] } =
    getAdminContentsLikesByContentTypeCall.hook();

  const contentHash = useCallback(
    (metric: string) => {
      return (
        (responseGetAdminContentsLikesByContentType &&
          responseGetAdminContentsLikesByContentType.find(
            (interaction: { content?: Content; contentHash?: string }) =>
              interaction.content?.metadata?.metric === metric,
          )?.contentHash) ||
        ''
      );
    },
    [responseGetAdminContentsLikesByContentType],
  );

  const totalInterestedPeople = useCallback(
    (metric: string) => {
      return (
        (responseGetAdminContentsLikesByContentType &&
          (
            responseGetAdminContentsLikesByContentType as {
              content?: Content;
              likesCount?: number;
            }[]
          ).find(interaction => interaction.content?.metadata?.metric === metric)?.likesCount) ||
        0
      );
    },
    [responseGetAdminContentsLikesByContentType],
  );

  const onOpenDialogCourse = useCallback(
    (slide: CardItem) => {
      setParameters({
        title: slide.title,
        totalInterestedPeople: totalInterestedPeople(slide.title),
        longDescription: slide.longDescription,
        shortDescription: slide.shortDescription,
        color1: slide.color1,
        color2: slide.color2,
        linkBrochure: slide.linkBrochure,
        contentHash: contentHash(slide.title),
      });
      openCourseDetailsDialog();
    },
    [contentHash, openCourseDetailsDialog, totalInterestedPeople],
  );

  const onOpenDialogUsers = useCallback(
    (slide: CardItem) => {
      setParameters({
        title: slide.title,
        totalInterestedPeople: totalInterestedPeople(slide.title),
        longDescription: slide.longDescription,
        shortDescription: slide.shortDescription,
        color1: slide.color1,
        color2: slide.color2,
        linkBrochure: slide.linkBrochure,
        contentHash: contentHash(slide.title),
      });
      openUsersDetailsDialog();
    },
    [contentHash, openUsersDetailsDialog, totalInterestedPeople],
  );

  return (
    <>
      <Box mt='xxl'>
        <Box>
          <Box mb='xxs' display='flex' alignItems='center'>
            <Typography variant='h1'>{t('title')}</Typography>
            <Tooltip iconProps={{ size: 'xs', fill: 'grey/light' }} containerProps={{ ml: 'xxs' }}>
              {t('tooltip')}
            </Tooltip>
          </Box>
        </Box>
        <Box>
          <Slider.SliderBottomRightArrow
            slideToShow={{
              desktopLarge: elementToView?.desktopLarge || undefined,
              desktop: elementToView?.desktop || undefined,
              tablet: elementToView?.tablet || undefined,
              mobile: elementToView?.mobile || undefined,
            }}
          >
            {metricsMap
              .sort((a, b) => (a?.score ?? 0) - (b?.score ?? 0))
              .map((slide, index) => {
                return (
                  <Card p='xxxs' minHeight='200px' key={slide.title}>
                    <Box
                      background={`conic-gradient(from 120deg at 25% 40%, ${slide.color2} 0deg, ${slide.color1} 300deg)`}
                      borderRadius='l'
                      height={200}
                      display='flex'
                      flexDirection='column'
                      justifyContent='space-between'
                    >
                      <Box
                        display='flex'
                        flexDirection='row'
                        justifyContent='space-between'
                        alignItems='center'
                        ml='xs'
                        mr='xs'
                        mt='xs'
                      >
                        {index === 0 || index === 1 ? (
                          <Box
                            px='xxs'
                            borderRadius='xl'
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            }}
                          >
                            <Typography color='white' variant='p3/label'>
                              {t('card.skillToBoost')}
                            </Typography>
                          </Box>
                        ) : (
                          <Box />
                        )}
                        <Box
                          display='flex'
                          alignItems='center'
                          px='xxs'
                          borderRadius='xl'
                          backgroundColor='white'
                          onClick={
                            totalInterestedPeople(slide.title) > 0
                              ? () => onOpenDialogUsers(slide)
                              : undefined
                          }
                          style={
                            totalInterestedPeople(slide.title) < 1
                              ? { cursor: 'not-allowed' }
                              : { cursor: 'pointer' }
                          }
                        >
                          <Svg name='like' fill='primary' size='xs' />{' '}
                          <Typography variant='p2/label' color='primary'>
                            {totalInterestedPeople(slide.title)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography mb='xs' ml='xs' variant='h2'>
                        {slide.title}
                      </Typography>
                    </Box>
                    <Box p='xxs'>
                      <Typography variant='p3/light' minHeight='80px'>
                        {slide.shortDescription}
                      </Typography>
                      {/* <Box mt='xs' display='flex' alignItems='center' flexDirection='row'>
                        <Box flex='1' textAlign='left'>
                          <Typography variant='p3/light'>
                            <Box display='flex' alignItems='center'>
                              <Svg name='user' size='xs' />
                              <Typography ml='xxs' variant='p2/light'>
                                {t('card.initialLevel')}
                              </Typography>
                              <Tooltip
                                iconProps={{ size: 'xs', fill: 'grey/light' }}
                                containerProps={{ ml: 'xxxs' }}
                              >
                                {t('card.partecipanesTooltip')}
                              </Tooltip>
                            </Box>
                          </Typography>
                        </Box>
                        <Box flex='1' textAlign='right'>
                          <Typography variant='p3/light'>
                            {slide.peopleWithInitialLevel}{' '}
                            {Number(slide.peopleWithInitialLevel) === 1
                              ? t('card.participant')
                              : t('card.participants')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box mt='xxs' display='flex' alignItems='center' flexDirection='row'>
                        <Box flex='1' textAlign='left'>
                          <Typography variant='p3/light'>
                            <Box display='flex' alignItems='center'>
                              <Svg name='departments' size='xs' />
                              <Typography ml='xxs' variant='p2/light'>
                                {t('card.departments')}
                              </Typography>
                              <Tooltip
                                iconProps={{ size: 'xs', fill: 'grey/light' }}
                                containerProps={{ ml: 'xxxs' }}
                              >
                                {t('card.departmentsTooltip')}
                              </Tooltip>
                            </Box>
                          </Typography>
                        </Box>
                        <Box flex='1' textAlign='right'>
                          <Typography variant='p3/light'>{slide.totalDepartments}</Typography>
                        </Box>
                      </Box> */}
                      <Button onClick={() => onOpenDialogCourse(slide)} mt='s' variant='positive'>
                        {t('card.buttonLabel')}
                      </Button>
                    </Box>
                  </Card>
                );
              })}
          </Slider.SliderBottomRightArrow>
        </Box>
      </Box>
      {parameters && <CourseDetailsDialog item={parameters} />}
      {parameters && <UsersContentsLikesDialog item={parameters} />}
    </>
  );
};
